import { useState } from 'react';
import { Boxes, ReceiptText } from 'lucide-react';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Skeleton } from '@vnetwork/ui/primitives';
import {
  useChildServices,
  useChildInvoices,
  type ChildService,
  type ChildInvoice,
} from '@/api/organization';
import { statusTone, titleCase, formatDate, formatCurrency } from '../shared';

const PAGE_SIZE = 20;

function TableSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} style={{ height: 44 }} />
      ))}
    </div>
  );
}

const customerCell = (name: string, email: string) => (
  <span style={{ display: 'grid', lineHeight: 1.3 }}>
    <span style={{ fontWeight: 500 }}>{name || '—'}</span>
    {email && <span style={{ color: 'var(--vn-fg-muted)', fontSize: 12 }}>{email}</span>}
  </span>
);

export function ChildServicesTable() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useChildServices({ page, pageSize: PAGE_SIZE });
  const rows = data?.results ?? [];
  const pagination = data?.pagination;

  const columns: ColumnDef<ChildService, unknown>[] = [
    { id: 'service', header: 'Service', accessorKey: 'service_name', cell: ({ row }) => row.original.service_name || '—' },
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => customerCell(row.original.customer_name, row.original.customer_email),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <StatusPill tone={statusTone(row.original.service_status)}>
          {titleCase(row.original.service_status || 'unknown')}
        </StatusPill>
      ),
    },
    {
      id: 'price',
      header: 'Recurring',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(row.original.price_recurring)}</span>
      ),
    },
  ];

  if (isLoading) return <TableSkeleton />;
  if (rows.length === 0)
    return <EmptyState icon={<Boxes size={28} />} title="No customer services" description="Services owned by your customers will appear here." />;

  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowId={(r) => String(r.service_id)}
      footer={
        pagination && pagination.total_pages > 1 ? (
          <Pagination page={pagination.page} pageCount={pagination.total_pages} onPageChange={setPage} summary={`Showing ${rows.length} of ${pagination.total} services`} />
        ) : undefined
      }
    />
  );
}

export function ChildInvoicesTable() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useChildInvoices({ page, pageSize: PAGE_SIZE });
  const rows = data?.results ?? [];
  const pagination = data?.pagination;

  const columns: ColumnDef<ChildInvoice, unknown>[] = [
    {
      id: 'invoice',
      header: 'Invoice',
      cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>#{row.original.invoice_id}</span>,
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => customerCell(row.original.customer_name, row.original.customer_email),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <StatusPill tone={statusTone(row.original.invoice_status)}>
          {titleCase(row.original.invoice_status || 'unknown')}
        </StatusPill>
      ),
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(row.original.invoice_total)}</span>
      ),
    },
    {
      id: 'due',
      header: 'Due',
      cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{formatDate(row.original.due_date)}</span>,
    },
  ];

  if (isLoading) return <TableSkeleton />;
  if (rows.length === 0)
    return <EmptyState icon={<ReceiptText size={28} />} title="No customer invoices" description="Invoices billed to your customers will appear here." />;

  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowId={(r) => String(r.invoice_id)}
      footer={
        pagination && pagination.total_pages > 1 ? (
          <Pagination page={pagination.page} pageCount={pagination.total_pages} onPageChange={setPage} summary={`Showing ${rows.length} of ${pagination.total} invoices`} />
        ) : undefined
      }
    />
  );
}
